import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, Lock } from "lucide-react";
import { api, errMsg, downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { rp } from "../lib/format";
import { PageHeader } from "../components/common";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const P = ({ k, v }) => (<div className="flex justify-between border-b border-slate-100 py-2.5 text-sm last:border-0"><span className="text-slate-500">{k}</span><span className="font-semibold text-[#17223B] lp-num">{v}</span></div>);

function Params() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/calc/params").then((r) => setP(r.data)); }, []);
  if (!p) return null;
  return (
    <section className="lp-card p-6" data-testid="calc-params-panel">
      <div className="mb-4 flex items-center justify-between"><h2 className="font-display font-bold">Parameter Resmi Calc Platinum</h2>
        <span className="flex items-center gap-1 rounded-full bg-[#078D67]/10 px-2.5 py-0.5 text-xs font-semibold text-[#078D67]"><Lock className="h-3 w-3" />{p.formula_version} • terkunci</span></div>
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        {p.categories.map((c) => (
          <div key={c.name} className="rounded-xl bg-[#F5F6FA] p-4">
            <p className="font-display font-bold text-[#351080]">{c.name}</p>
            <p className="mt-1 text-xs text-slate-500">Usia ≤ {c.max_age} th</p>
            <p className="mt-2 text-sm">Bunga koperasi <b>{c.coop_rate_annual}%</b></p><p className="text-sm">Cadangan risiko <b>{c.risk_pct}%</b></p>
          </div>
        ))}
      </div>
      <P k="Bunga bank" v={`${p.bank_rate_annual}% per tahun`} /><P k="Batas usia akhir kredit" v="84 tahun 10 bulan" />
      <P k="Usia masuk ditolak" v={`≥ ${p.reject_age} tahun`} /><P k="Tenor maksimal" v={`${p.max_tenor} bulan`} />
      <P k="Plafond maksimal absolut" v={rp(p.max_plafond)} /><P k="Rasio angsuran maksimal" v={`${p.max_ratio_pct}% gaji`} />
      <P k="Biaya PROAQ" v={`${p.proaq_pct}% plafond`} /><P k="Biaya administrasi" v={rp(p.admin_fee)} />
      <P k="Tata Laksana" v={`0% – ${String(p.tata_laksana_max_pct).replace(".", ",")}%`} /><P k="Retensi angsuran" v={`${p.retention_multiplier}× angsuran koperasi`} />
      <p className="mt-3 text-xs text-slate-400">Parameter mengikuti ketentuan AMS2 versi final dan tidak dapat diubah dari aplikasi tanpa persetujuan.</p>
    </section>
  );
}

function ChangePassword() {
  const [v, setV] = useState({ current_password: "", new_password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", v); toast.success("Kata sandi diperbarui"); setV({ current_password: "", new_password: "" }); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="lp-card space-y-4 p-6" data-testid="change-password-form">
      <h2 className="font-display font-bold">Ubah Kata Sandi</h2>
      <div className="space-y-1.5"><Label htmlFor="cp1">Kata sandi saat ini</Label><Input id="cp1" type="password" required value={v.current_password} onChange={(e) => setV({ ...v, current_password: e.target.value })} data-testid="current-password-input" /></div>
      <div className="space-y-1.5"><Label htmlFor="cp2">Kata sandi baru (min. 8)</Label><Input id="cp2" type="password" required minLength={8} value={v.new_password} onChange={(e) => setV({ ...v, new_password: e.target.value })} data-testid="new-password-input" /></div>
      <Button type="submit" className="bg-[#351080] hover:bg-[#2a0c66]" data-testid="change-password-submit">Simpan</Button>
    </form>
  );
}

function Backup() {
  const ref = useRef();
  const restore = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post("/backup/restore", fd); toast.success(`Dipulihkan: ${Object.values(data.restored).reduce((a, b) => a + b, 0)} data`); } catch (e) { toast.error(errMsg(e)); } finally { ref.current.value = ""; }
  };
  return (
    <section className="lp-card space-y-4 p-6" data-testid="backup-panel">
      <h2 className="font-display font-bold">Backup & Pemulihan</h2>
      <p className="text-sm text-slate-500">Unduh salinan JSON seluruh leads, aktivitas, simulasi, impor, dan audit log. Pemulihan menggabungkan data berdasarkan ID (tidak menghapus data yang ada).</p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => downloadFile("/backup", "backup.json")} data-testid="backup-download-button"><Download className="mr-2 h-4 w-4" />Unduh Backup</Button>
        <input ref={ref} type="file" accept=".json" className="hidden" onChange={(e) => restore(e.target.files[0])} data-testid="restore-file-input" />
        <Button variant="outline" onClick={() => ref.current.click()} data-testid="restore-button"><Upload className="mr-2 h-4 w-4" />Pulihkan dari File</Button>
      </div>
    </section>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  return (
    <div>
      <PageHeader eyebrow="Pengaturan" title="Pengaturan" desc="Parameter kalkulator, keamanan akun, dan backup data." />
      <div className="grid gap-6 xl:grid-cols-[1.4fr_1fr]">
        <Params />
        <div className="space-y-6"><ChangePassword />{user.role === "super_admin" && <Backup />}</div>
      </div>
    </div>
  );
}
